export default {
  title: 'Invoices',
  description: 'Manage billing and invoices',

  // Labels
  labels: {
    invoice_number: 'Invoice Number',
    billed_date: 'Billed Date',
    due_date: 'Due Date',
    total: 'Total',
    subtotal: 'Subtotal',
    vat: 'VAT {{rate}}%',
    balance_due: 'Balance Due',
    paid_amount: 'Paid',
    payment_difference: 'Payment difference of {{currency}}{{amount}} from invoice total',
    lease: 'Lease',
    customer: 'Customer',
    unit: 'Unit',
    billing_period: 'Billing Period',
    due_by: 'Due {{date}}',
    signed_at: 'Signed at',
    lease_option: '{{code}} - {{name}} (Unit {{unit}})',
    external_customer_fallback: 'Customer #{{id}}',
  },

  // Status
  status: {
    all: 'All Invoices',
    paid: 'Paid',
    unpaid: 'Unpaid',
    overdue: 'Overdue',
    partially_paid: 'Partially paid',
    paid_so_far: 'Paid {{amount}}',
    fully_paid: 'Fully Paid',
    payment_overdue: 'Payment Overdue',
    payment_due: 'Payment Due',
  },

  // Item types
  item_types: {
    rent: 'Rent',
    water: 'Water',
    electricity: 'Electricity',
    internet: 'Internet',
    common_fee: 'Common Fee',
    parking: 'Parking',
    discount: 'Discount',
    other: 'Other',
    common_area_fee: 'Common Area Fee',
    late_fee: 'Late Fee',
    promotion: 'Promotion',
    // EP-111: pricing_calculator.rb hardcodes this item_type on every
    // co-owner CAF invoice line that carries a positive vat_rate.
    common_area_fee_vat: 'Common Area Fee VAT',
    // EP-130: the Bill-deposit action (or a manually typed line) journals to
    // the security_deposit liability, never revenue.
    deposit: 'Security deposit',
    // EP-136: booking_fee is deposit money (security_deposit liability, same
    // as `deposit` above); reservation_fee is a non-refundable revenue line
    // and never counts toward the deposit.
    booking_fee: 'Booking Fee',
    reservation_fee: 'Reservation Fee',
  },

  // Payment
  payment: {
    title: 'Payments',
    record: 'Record Payment',
    sections: {
      advanced: 'Additional details',
    },
    amount: 'Amount ({{currency}})',
    method: 'Payment Method',
    date: 'Payment Date',
    date_before_invoice_warning: 'This date is before the invoice date ({{date}})',
    date_future_warning: 'This date is in the future',
    reference: 'Note / Reference (optional)',
    reference_placeholder: 'Transaction ID, slip number, etc.',
    confirmed_at: 'Confirmed date',
    confirmed_at_hint: 'When payment was confirmed received. Leave empty if not yet confirmed. Setting it counts the payment toward the invoice.',
    methods: {
      cash: 'Cash',
      transfer: 'Bank Transfer',
      promptpay: 'PromptPay',
      credit_card: 'Credit Card',
    },
    enable_method_hint: 'Need another payment method? Enable it at',
    enable_method_link: 'Settings → Payment methods',
    slip: 'Slip',
    slip_required: 'Slip image is required for bank transfer',
    // F10/AC-8 (EP-158): the browser's native "Choose File"/"No file
    // chosen" text is OS-locale, not CSS-themeable — FileInput's opt-in
    // overlay (chooseLabel/emptyLabel) replaces it with these.
    slip_choose_file: 'Choose slip file',
    slip_no_file: 'No file chosen',
    verified_locked_hint: 'Amount and method are locked because this payment is verified. To change them, reject the slip first.',
    missing_slip: 'Missing Slip',
    download_receipt: 'Receipt',
    delete_confirm: 'Delete this payment?',
    slip_preview_alt: 'Slip preview',
    preview_receipt: 'Preview Receipt PDF',
    no_receipt_template: 'No receipt template found. Please configure one in Settings → Templates.',
    invoice_not_loaded: 'Invoice still loading. Please wait a moment and try again.',
  },

  // Actions
  actions: {
    new_invoice: 'New Invoice',
    create_invoice: 'Create Invoice',
    edit_invoice: 'Edit Invoice',
    update_invoice: 'Update Invoice',
    add_item: 'Add Item',
    generate: 'Generate Invoices',
    back_to_invoices: 'Back to Invoices',
    back_to_invoice: 'Back to Invoice',
    download_pdf: 'Download PDF',
    print_selected: 'Print Selected ({{count}})',
    print_all: 'Print All',
    print_all_description: 'Generate invoices for all {{count}} filtered records and open as PDF in a new tab.',
    create_parking_invoice: 'Create Parking Invoice',
    sign: 'Sign Invoice',
    re_sign: 'Re-sign',
    download_signed_pdf: 'Download Signed PDF',
    download_csv: 'Download CSV',
    preview_pdf: 'Preview Invoice PDF',
  },

  // EP-56 L5 — CSV export column headers
  csv: {
    key: 'Key',
    description: 'Description',
    type: 'Type',
    quantity: 'Quantity',
    unit_price: 'Unit Price',
    amount: 'Amount',
    total: 'TOTAL',
    percentage_basis: '{{rate}}% of subtotal',
  },

  // External parker
  external_parker_invoice: 'Parking Invoice',

  // Print dialog
  print: {
    title: 'Print Invoices',
    generate: 'Generate PDF',
    generating: 'Generating...',
  },

  // Items table
  items: {
    title: 'Invoice Items',
    description: 'Description',
    type: 'Type',
    // F16/AC-10 (EP-158): the mobile card heading for a single line item —
    // was reusing `type` + an index, reading "Type 1".
    item_label: 'Item {{n}}',
    quantity: 'Qty',
    unit_price: 'Unit Price',
    amount: 'Amount',
    percentage_basis: '{{rate}}% of subtotal',
  },

  // Detail
  detail: {
    title: 'Invoice Details',
    billed_for_period: 'Issued {{date}}, for {{period}}',
  },

  // Form
  form: {
    title_new: 'New Invoice',
    title_edit: 'Edit Invoice',
    description_new: 'Create a new billing invoice',
    description_edit: 'Edit invoice details',
    invoice_details: 'Invoice Details',
    select_lease: 'Search lease...',
    default_rent_description: 'Monthly Rent',
    loading_pricing: 'Loading pricing...',
    error_zero_total: 'Invoice total cannot be zero. Please adjust item amounts.',
    metered_zero_qty_hint: 'Add a current meter reading to auto-fill consumption',
    // MON-8 E / T149-100: shown, non-blocking, when the due date is earlier
    // than the issue date (billed_at). Legitimate under MON-10 (billed_at
    // is the real issue date), just worth flagging.
    due_before_issue_warning: 'The due date is before the invoice\'s issue date.',
    // T149-022 (LSE-7 decision A): the owner's restricted-edit notice on a
    // paid invoice. Only billed_at/due_at/billing_period may change.
    restricted_edit_notice: 'Only the billed date, due date and billing period can be edited. Items and the total cannot be changed.',
    sections: {
      advanced: 'Line items (auto-generated)',
    },
  },

  // Filters
  filters: {
    date_from: 'From',
    date_to: 'To',
    all_periods: 'All Periods',
    custom_range: 'Custom Range',
    search_placeholder: 'Search invoice number...',
  },

  // Setup guide
  setup_guide: {
    title: 'Get started with invoicing',
    description: 'Create invoices to track billing and payments for your tenants.',
    prereq_lease: 'At least one active lease',
    prereq_setup_lease: 'Create Lease',
    prerequisites_title: 'Before you begin',
    step_1_title: 'Create an invoice',
    step_1_description: 'Select a lease and add billing items like rent, water, and electricity.',
    step_2_title: 'Record payments',
    step_2_description: 'Track payments as tenants pay their bills.',
    step_3_title: 'Generate in batch',
    step_3_description: 'Use batch generation to create invoices for all active leases at once.',
    preview_sample: 'Preview a sample invoice',
    import: 'Import',
    import_off_scope_warning: 'Found {{count}} {{entity}} rows. Import them from the {{Entity}} page.',
    download_sample: 'Download sample',
    seed_sample: 'Seed sample data',
    seed_sample_confirm: 'This will create: 1 property + 1 unit + 1 customer + 1 lease + 1 invoice + 1 payment. Continue?',
    seed_sample_success: 'Sample invoice (with full data chain) created successfully.',
    seed_sample_noop: 'You already have invoices. Sample data was not added.',
  },

  // Empty
  empty: {
    title: 'No invoices found',
    description: 'Create your first invoice.',
    not_found: 'Invoice not found',
    // B-017: shown when invoices EXIST in the account but the active filters
    // (property / status / dates) return nothing — never the first-run guide.
    no_match_title: 'No invoices match your filters',
    no_match_description: 'Try clearing the property, status, or date filters to see all invoices.',
    clear_filters: 'Clear filters',
  },

  // Messages
  messages: {
    delete_confirm: 'Delete this invoice? This action cannot be undone.',
    error_loading: 'Error loading invoices',
    error_description: 'There was an error loading the invoice list.',
    // T149-022 (LSE-7): shown inline on /invoices/:id/edit to anyone but the
    // owner when the invoice has payments, instead of a silent redirect.
    cannot_edit_paid: 'This invoice has payments and cannot be edited.',
    sign_success: 'Invoice signed successfully',
    signed_stale: 'Invoice has been modified since signing. The signed PDF may be outdated.',
    signed_stale_badge: 'Outdated',
    no_invoice_template: 'No invoice template found. Please configure one in Settings → Templates.',
  },

  // Wizard preview
  preview: {
    draft_number: 'DRAFT (preview)',
  },

  // Tax invoice
  tax_invoice: {
    title: 'Tax Invoice',
    number: 'Tax Invoice Number',
    date: 'Tax Invoice Date',
    generate: 'Generate Tax Invoice',
    generate_confirm: 'Generate a tax invoice for this invoice? This will assign a tax invoice number and cannot be changed.',
    already_generated: 'Tax invoice already generated',
    no_tax_id: 'Please configure your Tax ID in Settings before generating tax invoices.',
    no_tax_id_hint: 'Tax ID is required to issue a tax invoice. Click to go to Settings → Account and add your 13-digit Tax ID.',
    seller_section: 'Seller Information (ข้อมูลผู้ขาย)',
    buyer_section: 'Buyer Information (ข้อมูลผู้ซื้อ)',
    seller_name: 'Name (ชื่อ)',
    seller_tax_id: 'Tax ID (เลขประจำตัวผู้เสียภาษี)',
    seller_address: 'Address (ที่อยู่)',
    seller_branch: 'Branch Number (รหัสสาขา)',
    buyer_name: 'Name (ชื่อ)',
    buyer_tax_id: 'Tax ID (เลขประจำตัวผู้เสียภาษี)',
    buyer_address: 'Address (ที่อยู่)',
    download_pdf: 'Download PDF',
    preview_pdf: 'Preview PDF',
    vat_changed: 'VAT settings have changed since this tax invoice was generated. Please regenerate the PDF.',
    regenerate_pdf: 'Regenerate PDF',
  },

  // Sign modal
  sign_modal: {
    description: 'Draw your signature below to sign the invoice',
    generating: 'Generating PDF...',
    signing: 'Signing...',
    progress: 'Signing {{current}} of {{total}}...',
  },

  // Bulk actions
  bulk: {
    select_all: 'Select all',
    selected_count: '{{count}} selected',
    sign: 'Sign',
    // EP-54: split into i18next v25 plural forms; base key removed.
    sign_success_one: '{{count}} invoice signed successfully',
    sign_success_other: '{{count}} invoices signed successfully',
    sign_partial: '{{count}} of {{total}} invoices signed (some failed)',
    clear_selection: 'Clear',
  },

  // Batch generation
  batch: {
    title: 'Generate Invoices',
    description: 'Generate invoices for all active leases',
    period: 'Billing Period',
    due_date: 'Due Date',
    preview: 'Preview',
    generate: 'Generate All',
    // EP-54: split into i18next v25 plural forms; base key removed.
    generate_confirm_one: 'Generate {{count}} invoice?',
    generate_confirm_other: 'Generate {{count}} invoices?',
    missing_readings: 'Missing Readings',
    negative_usage: 'Negative meter usage',
    move_in_marker: 'Moves in {{date}}',
    move_out_marker: 'Moves out {{date}}',
    full_month_caption: 'Bills the full month without proration. Deselect this lease and create an invoice manually if an adjustment is needed.',
    select_lease: 'Select {{lease}}',
    total_leases: 'Total Leases',
    total_amount: 'Total Amount',
    no_active_leases: 'No active leases found for this period.',
    already_invoiced: 'Already invoiced',
    already_invoiced_count: '{{count}} already invoiced',
    to_generate_one: '{{count}} lease to invoice',
    to_generate_other: '{{count}} leases to invoice',
    // F4: selective batch generation
    generate_selected_one: 'Generate {{count}} invoice',
    generate_selected_other: 'Generate {{count}} invoices',
    selected_summary: 'Selected {{count}} of {{eligible}} · {{total}}',
    no_selection_disabled_hint: 'Tick at least one lease to enable',
    clear_selection: 'Reset to all',
    // EP-149 T149-016 (MON-8): due-date override toggle, the per-row due date
    // column, and the past-due warning banner.
    override_due_date_toggle: 'Set due date manually',
    due_date_column: 'Due Date',
    due_date_warning_title: 'Invoices already past due',
    due_date_warning_body: 'Some of the invoices being generated are already past due.',
    due_date_warning_late_fee: 'Late fees start from {{date}}.',
    keep_dates: 'Keep the dates',
    push_days_label: 'Push to issue date +',
    push_days_suffix: 'days',
    push_days_apply: 'Apply',
    // MON-8 E / T149-100: shown next to the override due-date input when the
    // chosen date is before today (the issue date a generate run would
    // stamp). Row-level past-due dates already surface via the warning
    // banner above, so this is override-only, not per row.
    due_before_issue_warning: 'The due date is before the invoice\'s issue date.',
  },

  // Server-side error codes (returned in error.code by the API).
  errors: {
    missing_lease_or_customer: 'Invoice must be linked to either a lease or a customer.',
    // T149-021/T149-022 (LSE-7): the three 422 codes invoices_controller.rb
    // returns on a paid invoice's PATCH.
    cannot_edit_paid_invoice: 'Only the owner may edit an invoice that has payments.',
    cannot_edit_amounts_on_paid_invoice: 'Only the billed date, due date and billing period can be changed on an invoice with payments.',
    invoice_repost_failed: 'Could not save the invoice: reposting its journal entry failed.',
    validation_failed: 'Validation failed. Please check the form and try again.',

    // Per-field labels used when error.details points at specific fields.
    fields: {
      description: 'Description',
      item_type: 'Item type',
      quantity: 'Quantity',
      unit_price: 'Unit price',
      amount: 'Amount',
      lease: 'Lease',
      lease_id: 'Lease',
      customer: 'Customer',
      customer_id: 'Customer',
      billed_at: 'Billing date',
      due_at: 'Due date',
      total: 'Total',
    },

    // Rails AR error codes (the symbols inside record.errors.details).
    codes: {
      blank: "can't be blank",
      taken: 'is already taken',
      invalid: 'is invalid',
      too_short: 'is too short',
      too_long: 'is too long',
      greater_than: 'must be greater than the limit',
      greater_than_or_equal_to: 'must be greater than or equal to the limit',
      less_than: 'must be less than the limit',
      less_than_or_equal_to: 'must be less than or equal to the limit',
      not_a_number: 'is not a number',
      inclusion: 'is not included in the list',
      exclusion: 'is reserved',
      confirmation: "doesn't match",
    },
  },
}
